import React, { useRef, useEffect, useState, useCallback } from 'react';

import { Container, useIsBreakpointUp } from '@soluto-design/react';

import BlockTitleLarge, {
    BlockTitleLargeProps,
} from '../../molecules/BlockTitleLarge';

import cls from '@soluto-design/styles/cls';

export interface HeroHomeSectionProps
    extends Omit<BlockTitleLargeProps, 'rating'> {
    media?: string;
    mediaMobile?: string;
    glbPath?: string;
    hotspots?: string[];
}

export default function HeroHomeSection({
    media,
    mediaMobile,
    glbPath,
    hotspots,
    ...props
}: HeroHomeSectionProps) {
    const modelViewerRef = useRef();
    const isLg = useIsBreakpointUp('lg');
    const [start, setStart] = useState(false);

    useEffect(() => {
        if (!modelViewerRef?.current || !isLg || !start) {
            return;
        }

        if (hotspots?.length) {
            modelViewerRef.current.innerHTML =
                modelViewerRef.current.innerHTML + hotspots.join('');
        }

        (modelViewerRef.current as any)?.dismissPoster();
    }, [modelViewerRef, isLg, start, hotspots]);

    const onMouseEnter = useCallback(() => {
        setStart(true);
    }, [setStart]);

    return (
        <section
            {...(!!isLg && { onMouseEnter })}
            className={cls(
                'bg-background-secondary lg:bg-transparent overflow-hidden relative pt-120 z-0 lg:pt-0 lg:min-h-screen flex flex-col lg:before:!content-none',
            )}>
            <Container className="lg:pr-0 lg:flex lg:items-stretch flex-1">
                <div className="z-20 row gap-y-[58px] md:gap-y-[148px] flex-1">
                    <div className="z-30 py-sm lg:py-xxxl col-span-full lg:col-span-4 xl:col-span-6 hd:col-span-5 flex flex-col items-start justify-center">
                        <BlockTitleLarge
                            heroProportions
                            buttonSecondaryVariation
                            titleTag="h1"
                            preTitleTag="p"
                            {...props}
                        />
                    </div>
                    <div className="col-span-full lg:col-span-4 xl:col-span-6 hd:col-span-7 flex items-center lg:justify-center lg:items-center relative z-10 shapes-hover-effect">
                        <div className="relative h-[375px] md:h-[550px] lg:h-full w-full">
                            {glbPath && typeof window !== 'undefined' ? (
                                <model-viewer
                                    ref={modelViewerRef}
                                    src={glbPath}
                                    reveal="manual"
                                    shadow-intensity="0"
                                    shadow-softness="0"
                                    camera-controls
                                    auto-rotate
                                    ar
                                    ar-modes="webxr scene-viewer quick-look"
                                    orbit-sensitivity="0.5"
                                    zoom-sensitivity="3"
                                    pan-sensitivity="0.5"
                                    camera-orbit="30deg 70deg 0%"
                                    max-camera-orbit="Infinity 90deg auto"
                                    max-field-of-view="7deg"
                                    min-field-of-view="0.25deg"
                                    poster={
                                        mediaMobile && !isLg
                                            ? mediaMobile
                                            : media
                                    }
                                    environment-image="legacy"
                                    tone-mapping="neutral"
                                    class="absolute h-full w-auto inset-0 -right-margin -left-margin lg:left-[-10vw] lg:right-0 hd:left-0 hd:right-[-15vw] min-[2300px]:right-[-25vw] min-[3200px]:right-[-35vw] bg-base-200 model-viewer">
                                    {!!media && (
                                        <div
                                            slot="poster"
                                            className={cls(
                                                'absolute bg-cover bg-center inset-0 w-full h-full',
                                            )}
                                            style={{
                                                backgroundImage: `url(${
                                                    mediaMobile && !isLg
                                                        ? mediaMobile
                                                        : media
                                                })`,
                                            }}
                                        />
                                    )}
                                </model-viewer>
                            ) : media ? (
                                <div
                                    className={cls(
                                        'absolute bg-cover bg-center w-auto inset-0 top-1/4 -right-margin -left-margin lg:left-[-10vw] lg:top-0 lg:right-0 hd:left-0 hd:right-[-15vw] min-[2300px]:right-[-25vw] min-[3200px]:right-[-35vw]',
                                    )}
                                    style={{
                                        backgroundImage: `url(${
                                            mediaMobile && !isLg
                                                ? mediaMobile
                                                : media
                                        })`,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                    {/* right side shapes (lg) */}
                    <div className="absolute bottom-0 top-auto lg:top-0 lg:bottom-auto right-0 w-full h-[375px] md:h-[550px] lg:h-full z-20 right-side-shapes pointer-events-none">
                        <div className="relative w-full h-[25%] bg-brand-primary-900 -skew-x-[20deg] top-0 left-[20%] md:left-[10%] lg:left-[62%]" />
                        <div className="relative w-full h-[25%] bg-brand-primary-100 -skew-x-[20deg] top-0 left-[85%] lg:left-[95%]" />
                        <div className="relative w-full h-[25%] bg-brand-primary-500 -skew-x-[20deg] top-0 left-[90%] lg:left-[95%]" />
                        <div className="relative w-full h-[25%] bg-brand-primary-700 -skew-x-[20deg] top-0 left-[75%] lg:left-[80%]" />
                    </div>

                    {/* left side shapes */}
                    <div className="absolute bottom-0 top-auto lg:top-0 lg:bottom-auto right-0 w-full h-[375px] md:h-[550px] lg:h-full z-20 left-side-shapes pointer-events-none">
                        <div className="relative w-full h-[75%] md:h-[62.5%]">
                            <div className="bg-background-secondary -skew-x-[20deg] absolute top-0 right-[80%] md:right-[90%] lg:right-[32%] w-full h-full" />
                        </div>
                        <div className="relative w-full h-[25%] md:h-[37.5%]">
                            <div className="bg-background-secondary -skew-x-[20deg] absolute top-0 right-[80%] md:right-[90%] lg:right-[38%] w-full h-full" />
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
}
